import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useGetPostById } from '@/features/api/hooks/grapevine-post/useGetPostById';
import { PostEditForm } from '@/features/post-editor/components/PostEditForm';
import { SenderChipContainer } from '@/features/sender-selection/components/SenderChipContainer';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { useNavigationHeader } from '@/navigation/hooks/useNavigationHeader';
import { NavBarType } from '@/navigation/types/navbar';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { CONTENT_MANAGER_ROLES } from '@/shared/components/organization/roles';
import { useOrganizationAccessFor } from '@/shared/components/organization/useOrganizationAccessFor';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = AppNativeStackScreenProps<typeof AppRoute.PostEditor>;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.surfaceContainerLow }
  });

export const PostEditorScreen: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const postId = route.params?.id;

  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });

  const { data: post, isLoading, isError, refetch } = useGetPostById(postId);
  const { data: organization, isLoading: isOrganizationLoading } =
    useGetOrganizationById(post?.organizationId);

  const organizationId = post?.organizationId;
  const isOrganizationPost = !isEmptyOrNull(organizationId);
  const sender: Sender =
    isOrganizationPost && organizationId
      ? { type: SenderType.ORGANIZATION, organizationId }
      : { type: SenderType.USER };

  const orgAccess = useOrganizationAccessFor(organization, {
    roles: CONTENT_MANAGER_ROLES
  });
  const permissionLoading = isOrganizationPost && isOrganizationLoading;
  const permissionDenied =
    isOrganizationPost && orgAccess !== OrganizationAccessState.Granted;

  const header = useMemo(
    () => (
      <NavigationBar
        type={NavBarType.MODAL_WITH_CLOSE}
        centerTitle
        direction="row-reverse"
        titleComponent={<SenderChipContainer sender={sender} disabled />}
      />
    ),
    [sender]
  );
  useNavigationHeader(navigation, header);

  if (!post || isLoading || permissionLoading) {
    return (
      <View style={styles.container}>
        <StateView
          isLoading={isLoading || permissionLoading}
          isError={isError}
          stateLabels={t('state', { returnObjects: true })}
          onRefetch={refetch}
          backgroundColor={theme.colors.surfaceContainerLow}
        />
      </View>
    );
  }

  if (permissionDenied) {
    return (
      <View style={styles.container}>
        <StateView
          isLoading={false}
          isError
          stateLabels={t('state', { returnObjects: true })}
          backgroundColor={theme.colors.surfaceContainerLow}
        />
      </View>
    );
  }

  return (
    <PostEditForm
      key={post.id}
      post={post}
      navigation={navigation}
      sender={sender}
    />
  );
};
